import React, { useState, Children } from 'react';
import { MicIcon, MailIcon, BookIcon, CalendarIcon } from 'lucide-react';
import { ConnectionPageLayout } from '../../components/connections/ConnectionPageLayout';
import { FilterSection } from '../../components/UI/FilterSidebar';
import { OpportunityItem } from '../../components/connections/OpportunityPreviewCard';
import { useDarkClasses } from '../../hooks/useDarkClasses';
import { useRoleTheme } from '../../hooks/useRoleTheme';
const ConnectWithNarrators = () => {
  const dk = useDarkClasses();
  const rt = useRoleTheme();
  const genres = ['Romance', 'Fantasy', 'Science Fiction', 'Mystery', 'Thriller', 'Horror', 'Historical Fiction', 'Young Adult', 'Literary Fiction', 'Non-Fiction', 'Biography', 'Self-Help', "Children's"];
  const [formData, setFormData] = useState({
    name: '',
    email: '',
    company: '',
    projectTitle: '',
    projectDescription: '',
    manuscriptLength: '',
    timeline: '',
    budget: '',
    voiceRequirements: '',
    genreInterests: [] as string[],
    accentRequirements: '',
    termsAgreed: false
  });
  const [submitted, setSubmitted] = useState(false);
  const handleInputChange = (e: React.ChangeEvent<HTMLInputElement | HTMLTextAreaElement | HTMLSelectElement>) => {
    const {
      name,
      value
    } = e.target;
    setFormData(prev => ({
      ...prev,
      [name]: value
    }));
  };
  const handleCheckboxChange = (e: React.ChangeEvent<HTMLInputElement>) => {
    const {
      name,
      checked
    } = e.target;
    setFormData(prev => ({
      ...prev,
      [name]: checked
    }));
  };
  const handleGenreChange = (genre: string) => {
    setFormData(prev => ({
      ...prev,
      genreInterests: prev.genreInterests.includes(genre) ? prev.genreInterests.filter(g => g !== genre) : [...prev.genreInterests, genre]
    }));
  };
  const handleSubmit = (e: React.FormEvent) => {
    e.preventDefault();
    setSubmitted(true);
  };
  const inputClass = `w-full px-3 py-2 border rounded-md focus:outline-none focus:ring-2 focus:ring-amber-500 focus:border-transparent ${dk.inputOnCard}`;
  const labelClass = `block text-sm font-medium mb-1 ${dk.textSecondary}`;
  const sectionHeadingClass = `text-xl font-semibold mb-4 flex items-center ${dk.textPrimary}`;
  // Filters
  const filterSections: FilterSection[] = [{
    id: 'sort',
    title: 'Sort By',
    type: 'radio',
    options: [{
      value: 'relevance',
      label: 'Relevance'
    }, {
      value: 'rating-desc',
      label: 'Highest Rated'
    }, {
      value: 'reviews-desc',
      label: 'Most Reviews'
    }],
    value: 'relevance',
    onChange: () => {}
  }, {
    id: 'availability',
    title: 'Availability',
    type: 'checkbox',
    options: [{
      value: 'open',
      label: 'Open for Booking'
    }, {
      value: 'limited',
      label: 'Limited Availability'
    }],
    value: [],
    onChange: () => {}
  }, {
    id: 'genre',
    title: 'Genre',
    type: 'checkbox',
    options: genres.map(g => ({
      value: g,
      label: g
    })),
    value: [],
    onChange: () => {}
  }, {
    id: 'voiceStyle',
    title: 'Voice Style',
    type: 'checkbox',
    options: [{
      value: 'warm',
      label: 'Warm'
    }, {
      value: 'authoritative',
      label: 'Authoritative'
    }, {
      value: 'versatile',
      label: 'Versatile'
    }, {
      value: 'character',
      label: 'Character-driven'
    }, {
      value: 'deep',
      label: 'Deep'
    }, {
      value: 'energetic',
      label: 'Energetic'
    }],
    value: [],
    onChange: () => {}
  }];
  // Opportunities
  const opportunities: OpportunityItem[] = [{
    id: '1',
    title: 'Fantasy Series Narration',
    poster: 'Marcus Chen',
    deadline: '2023-12-01',
    type: 'Royalty Share',
    description: 'Seeking experienced narrator for 3-book epic fantasy series. Dual POV preferred.'
  }, {
    id: '2',
    title: 'Romance Audiobook Production',
    poster: 'Sarah Jenkins',
    deadline: '2023-11-20',
    type: 'PFH',
    description: 'Looking for female narrator with British accent for contemporary romance novel.'
  }];
  const formContent = <form onSubmit={handleSubmit} className="space-y-6" data-id="element-11948" data-mp="ci1rf05kodp3">
      {/* Contact Information */}
      <div data-id="element-11949" data-mp="hgwg2iq80wh4">
        <h2 className={sectionHeadingClass} data-id="element-11950" data-mp="hheb5c15d4ng">
          <MailIcon className="h-5 w-5 text-amber-600 mr-2" data-id="element-11951" data-mp="hgcwkxkxa00z" />
          Contact Information
        </h2>
        <div className="grid grid-cols-1 md:grid-cols-2 gap-6" data-id="element-11952" data-mp="lkazzkha5wzu">
          <div data-id="element-11953" data-mp="qgsy41i3ux37">
            <label htmlFor="name" className={labelClass} data-id="element-11954" data-mp="qicj25ephwbb">
              Your Name *
            </label>
            <input type="text" id="name" name="name" value={formData.name} onChange={handleInputChange} className={inputClass} required data-id="element-11955" data-mp="2b16k5epuqar1" />
          </div>
          <div data-id="element-11956" data-mp="1x0djsdcvclg0">
            <label htmlFor="email" className={labelClass} data-id="element-11957" data-mp="1x0vps1pc2e14">
              Email Address *
            </label>
            <input type="email" id="email" name="email" value={formData.email} onChange={handleInputChange} className={inputClass} required data-id="element-11958" data-mp="2tote0mqxbizg" />
          </div>
          <div data-id="element-11959" data-mp="2fz80vszt1xzc">
            <label htmlFor="company" className={labelClass} data-id="element-11960" data-mp="2fxn6q4hekh20">
              Company/Publisher
            </label>
            <input type="text" id="company" name="company" value={formData.company} onChange={handleInputChange} className={inputClass} data-id="element-11961" data-mp="3devmz4wsnpko" />
          </div>
        </div>
      </div>

      {/* Project Details */}
      <div data-id="element-11962" data-mp="c0197887ah7">
        <h2 className={sectionHeadingClass} data-id="element-11963" data-mp="cjggg6hrkrj">
          <BookIcon className="h-5 w-5 text-amber-600 mr-2" data-id="element-11964" data-mp="3iru1va89363i" />
          Project Details
        </h2>
        <div className="space-y-4" data-id="element-11965" data-mp="3msdxuah383o2">
          <div data-id="element-11966" data-mp="3mrkxbr8q2aed">
            <label htmlFor="projectTitle" className={labelClass} data-id="element-11967" data-mp="3mr2xkkmupe1p">
              Book/Project Title *
            </label>
            <input type="text" id="projectTitle" name="projectTitle" value={formData.projectTitle} onChange={handleInputChange} className={inputClass} required data-id="element-11968" data-mp="38vh686t5ai64" />
          </div>
          <div data-id="element-11969" data-mp="33w4dj5flegvm">
            <label htmlFor="projectDescription" className={labelClass} data-id="element-11970" data-mp="y13b6qipits1">
              Project Description *
            </label>
            <textarea id="projectDescription" name="projectDescription" value={formData.projectDescription} onChange={handleInputChange} rows={4} className={inputClass} placeholder="Please describe your audiobook project in detail..." required data-id="element-11971" data-mp="122ydnycdjd7e" />
          </div>
          <div data-id="element-11972" data-mp="2rjacs3000mgc">
            <label htmlFor="manuscriptLength" className={labelClass} data-id="element-11973" data-mp="2rjk7g3vznhsd">
              Manuscript Length (word count) *
            </label>
            <input type="text" id="manuscriptLength" name="manuscriptLength" value={formData.manuscriptLength} onChange={handleInputChange} className={inputClass} placeholder="e.g., 80,000 words" required data-id="element-11974" data-mp="366vh208m0rpu" />
          </div>
        </div>
      </div>

      {/* Timeline and Budget */}
      <div data-id="element-11975" data-mp="1kxg6s7aytoax">
        <h2 className={sectionHeadingClass} data-id="element-11976" data-mp="1kx6ol5w5wlfs">
          <CalendarIcon className="h-5 w-5 text-amber-600 mr-2" data-id="element-11977" data-mp="1fxc17cblidzh" />
          Timeline and Budget
        </h2>
        <div className="grid grid-cols-1 md:grid-cols-2 gap-6" data-id="element-11978" data-mp="uztb6m04grcb">
          <div data-id="element-11979" data-mp="z1y057fmkzsm">
            <label htmlFor="timeline" className={labelClass} data-id="element-11980" data-mp="z1ejdu1pohg6">
              Project Timeline
            </label>
            <select id="timeline" name="timeline" value={formData.timeline} onChange={handleInputChange} className={inputClass} data-id="element-11981" data-mp="13u7owk507ttb">
              <option value="" data-id="element-11982" data-mp="c3bd7muojv7m">Select timeline</option>
              <option value="Less than 1 month" data-id="element-11983" data-mp="g2siaf672qc9">Less than 1 month</option>
              <option value="1-2 months" data-id="element-11984" data-mp="g558tgq0q90x">1-2 months</option>
              <option value="2-3 months" data-id="element-11985" data-mp="l1ndkglcm7d9">2-3 months</option>
              <option value="3+ months" data-id="element-11986" data-mp="q18dat8cejr1">3+ months</option>
              <option value="Flexible" data-id="element-11987" data-mp="q2t6aef0dums">Flexible</option>
            </select>
          </div>
          <div data-id="element-11988" data-mp="1wh2a5j9nax7u">
            <label htmlFor="budget" className={labelClass} data-id="element-11989" data-mp="1wjod15llcojq">
              Budget Range (per finished hour)
            </label>
            <select id="budget" name="budget" value={formData.budget} onChange={handleInputChange} className={inputClass} data-id="element-11990" data-mp="3lyfugl8029c8">
              <option value="" data-id="element-11991" data-mp="3c8e6dtfncqxb">Select budget range</option>
              <option value="Under $100 PFH" data-id="element-11992" data-mp="3c6td0752je11">Under $100 PFH</option>
              <option value="$100-$200 PFH" data-id="element-11993" data-mp="3791l3d4fbrwu">$100-$200 PFH</option>
              <option value="$200-$300 PFH" data-id="element-11994" data-mp="337y2wjywljsg">$200-$300 PFH</option>
              <option value="$300-$400 PFH" data-id="element-11995" data-mp="3364ylbbrjbnm">$300-$400 PFH</option>
              <option value="$400-$500 PFH" data-id="element-11996" data-mp="2y8btklm74nox">$400-$500 PFH</option>
              <option value="$500+ PFH" data-id="element-11997" data-mp="2t9s8sojc9hy5">$500+ PFH</option>
              <option value="Royalty Share" data-id="element-11998" data-mp="2taa7drth4898">Royalty Share</option>
              <option value="Royalty Share Plus" data-id="element-11999" data-mp="2oc0ddkjhjdp7">Royalty Share Plus</option>
              <option value="Flexible" data-id="element-12000" data-mp="rj6zhaayx7sd">Flexible / To be discussed</option>
            </select>
          </div>
        </div>
      </div>

      {/* Voice Requirements */}
      <div data-id="element-12001" data-mp="mhrcvdt56t7v">
        <h2 className={sectionHeadingClass} data-id="element-12002" data-mp="29k1tlxfcjf2r">
          <MicIcon className="h-5 w-5 text-amber-600 mr-2" data-id="element-12003" data-mp="29iqs649dijet" />
          Voice Requirements
        </h2>
        <div className="space-y-4" data-id="element-12004" data-mp="24k5ry4u4fzqd">
          <div data-id="element-12005" data-mp="24nbfhfn3qtjz">
            <label htmlFor="voiceRequirements" className={labelClass} data-id="element-12006" data-mp="2jhy4co4dg80c">
              Voice Type Requirements
            </label>
            <textarea id="voiceRequirements" name="voiceRequirements" value={formData.voiceRequirements} onChange={handleInputChange} rows={3} className={inputClass} placeholder="Describe the voice characteristics you're looking for..." data-id="element-12007" data-mp="2ek6dlxfgqrxq" />
          </div>
          <div data-id="element-12008" data-mp="1vsndpzgqjtzx">
            <label className={labelClass} data-id="element-12009" data-mp="1vs3tud7iyvj9">Genre</label>
            <div className="flex flex-wrap gap-2 mt-1" data-id="element-12010" data-mp="1vrc4vl8fnw1y">
              {genres.map(genre => <button type="button" key={genre} onClick={() => handleGenreChange(genre)} className={`px-3 py-1 rounded-full text-sm transition-all border ${formData.genreInterests.includes(genre) ? 'bg-amber-100 text-amber-800 border-amber-300' : `${dk.badgeNeutral} ${dk.border}`}`} data-id="element-12011" data-mp="181821g32k0c0">
                  {genre}
                </button>)}
            </div>
          </div>
          <div data-id="element-12012" data-mp="39t092iff640a">
            <label htmlFor="accentRequirements" className={labelClass} data-id="element-12013" data-mp="39si32u2ygbf6">
              Accent Requirements
            </label>
            <input type="text" id="accentRequirements" name="accentRequirements" value={formData.accentRequirements} onChange={handleInputChange} className={inputClass} placeholder="e.g., British, American, Australian, etc." data-id="element-12014" data-mp="1alu7ok00uq8" />
          </div>
        </div>
      </div>

      {/* Terms and Submit */}
      <div className={`pt-4 border-t ${dk.border}`} data-id="element-12015" data-mp="2h3ktdfct7my1">
        <div className="flex items-start mb-6" data-id="element-12016" data-mp="2h54dxi5i0k5m">
          <div className="flex items-center h-5" data-id="element-12017" data-mp="nkkml6c7jctp">
            <input id="termsAgreed" name="termsAgreed" type="checkbox" checked={formData.termsAgreed} onChange={handleCheckboxChange} className="h-4 w-4 text-amber-600 border-gray-300 rounded focus:ring-amber-500 cursor-pointer" required data-id="element-12018" data-mp="nkav1ebikxlw" />
          </div>
          <div className="ml-3 text-sm" data-id="element-12019" data-mp="4umewrcbnlql">
            <label htmlFor="termsAgreed" className={dk.textSecondary} data-id="element-12020" data-mp="4vnrx1cweiay">
              I agree to the{' '}
              <a href="#" className="text-amber-600 hover:text-amber-700 font-medium hover:underline" data-id="element-12021" data-mp="3w2qhxph02ijl">
                Terms of Service
              </a>{' '}
              and{' '}
              <a href="#" className="text-amber-600 hover:text-amber-700 font-medium hover:underline" data-id="element-12022" data-mp="1p9xsrakoi867">
                Privacy Policy
              </a>
            </label>
          </div>
        </div>
        <button type="submit" className="w-full md:w-auto bg-amber-600 hover:bg-amber-700 text-white py-2 px-6 rounded-md font-medium transition-colors" data-id="element-12023" data-mp="1srbz9uvlvlfd">
          Submit Request
        </button>
      </div>
    </form>;
  return <ConnectionPageLayout title="Connect with Narrators" subtitle="Submit your audiobook project details to find the perfect voice talent" icon={<MicIcon className="h-8 w-8" data-id="element-12025" data-mp="19v2igztbkgro" />} vendorType="narrators" filterSections={filterSections} opportunities={opportunities} opportunitiesLink="/narrators/opportunities" formContent={formContent} formCTALabel="Submit a Narration Request" formCTADescription="Can't find the right voice? Submit your project details and we'll match you with a narrator." data-id="element-12024" data-mp="26k1p6ae99ahr" />;
};
export default ConnectWithNarrators;